/**
 * Access tokens, for either browser extension.
 *
 * One token works in both extensions, so both cards show this same section:
 * name and make a token, copy it once, see every token and when it was last
 * used, and revoke one. It used to live on the queued card only, and the
 * connected card could make an unnamed token it could not show again.
 *
 * A working token's name can be changed in place (click it), for when the
 * name box was left empty before Generate.
 *
 * Revoked tokens stay out of the way: they are kept for the record but shown
 * only after "Show N revoked".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import Panel from './Panel';
import type { PersonalAccessToken, PersonalAccessTokenCreated } from '../../types';

const LINK = 'text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100';

function fmtDate(value: string | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

export default function AccessTokens() {
  const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<PersonalAccessTokenCreated | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  // The token whose name is being edited, and the text typed so far.
  const [editing, setEditingState] = useState<{ id: number; name: string } | null>(null);
  // Mirrors `editing` for saveName. Closing the box can also fire its blur, so
  // Enter would save twice and Escape would save after all; the ref is already
  // empty by then, so that second call does nothing.
  const editingRef = useRef<{ id: number; name: string } | null>(null);
  const setEditing = (next: { id: number; name: string } | null) => {
    editingRef.current = next;
    setEditingState(next);
  };

  const load = useCallback(async () => {
    try {
      setTokens(await api.getPersonalAccessTokens());
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not load the tokens' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setNote(null);
    try {
      setCreated(await api.createPersonalAccessToken(name.trim() || undefined));
      setName('');
      await load();
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not make a token' });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: number) => {
    if (!window.confirm('Revoke this token? Any extension using it stops working.')) return;
    setNote(null);
    try {
      await api.revokePersonalAccessToken(id);
      setNote({ ok: true, text: 'Token revoked.' });
      await load();
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not revoke the token' });
    }
  };

  const saveName = async () => {
    if (!editingRef.current) return;
    const { id, name: typed } = editingRef.current;
    setEditing(null);
    if ((tokens.find((t) => t.id === id)?.name || '') === typed.trim()) return;
    setNote(null);
    try {
      const renamed = await api.renamePersonalAccessToken(id, typed);
      setTokens((ts) => ts.map((t) => (t.id === id ? { ...t, name: renamed.name } : t)));
    } catch (e) {
      setNote({ ok: false, text: e instanceof Error ? e.message : 'Could not rename the token' });
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setNote({ ok: true, text: 'Token copied.' });
    } catch {
      setNote({ ok: false, text: 'Could not copy - select the token and copy it by hand.' });
    }
  };

  const active = tokens.filter((t) => !t.revoked_at);
  const revoked = tokens.filter((t) => t.revoked_at);

  const row = (t: PersonalAccessToken) => (
    <div key={t.id} className="px-4 py-3 flex items-center justify-between">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {editing?.id === t.id ? (
            <input
              autoFocus
              value={editing.name}
              onChange={(e) => setEditing({ id: t.id, name: e.target.value })}
              onBlur={saveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveName();
                if (e.key === 'Escape') setEditing(null);
              }}
              maxLength={255}
              placeholder="Token name (e.g. Home laptop)"
              aria-label="Token name"
              className="px-2 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-sm font-normal bg-white dark:bg-gray-900"
            />
          ) : t.revoked_at ? (
            t.name || 'Unnamed token'
          ) : (
            <button
              onClick={() => setEditing({ id: t.id, name: t.name || '' })}
              title="Click to rename"
              className={`text-left hover:underline decoration-dotted ${t.name ? '' : 'text-gray-500 dark:text-gray-400 italic'}`}
            >
              {t.name || 'Unnamed token - add a name'}
            </button>
          )}
          <code className="text-xs text-gray-400 dark:text-gray-500 font-mono">{t.token_prefix}…</code>
          {t.revoked_at && (
            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
              revoked
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Created {fmtDate(t.created_at)} · Last used {fmtDate(t.last_used_at)}
          {t.revoked_at && <> · Revoked {fmtDate(t.revoked_at)}</>}
        </div>
      </div>
      {!t.revoked_at && (
        <button
          onClick={() => revoke(t.id)}
          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
        >
          Revoke
        </button>
      )}
    </div>
  );

  return (
    <Panel
      title="Access tokens"
      hint="Paste one into the extension's popup. One token works in both extensions."
      testId="access-tokens"
    >
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Token name (e.g. Home laptop)"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
          />
          <button
            onClick={create}
            disabled={creating}
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Generate token'}
          </button>
        </div>
        {created && (
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                Copy your new token now - it won't be shown again
              </p>
              <button
                onClick={() => setCreated(null)}
                className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 text-xs"
              >
                Dismiss
              </button>
            </div>
            <div className="flex gap-2">
              <input
                readOnly
                value={created.token}
                onFocus={(e) => e.target.select()}
                className="flex-1 font-mono text-xs px-3 py-2 border border-amber-300 dark:border-amber-700 rounded bg-white dark:bg-gray-800"
              />
              <button onClick={copy} className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm">
                Copy
              </button>
            </div>
          </div>
        )}
        {note && (
          <p className={`text-xs ${note.ok ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
            {note.text}
          </p>
        )}
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {loading ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading…</div>
        ) : (
          <>
            {active.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {revoked.length ? 'No working tokens.' : 'No access tokens yet.'}
              </div>
            ) : (
              active.map(row)
            )}
            {revoked.length > 0 && (
              <div className="px-4 py-2">
                <button onClick={() => setShowRevoked((v) => !v)} className={LINK} aria-expanded={showRevoked}>
                  {showRevoked ? 'Hide revoked' : `Show ${revoked.length} revoked`}
                </button>
              </div>
            )}
            {showRevoked && revoked.map(row)}
          </>
        )}
      </div>
    </Panel>
  );
}
